import type {
  CodeableConcept,
  ContactPoint,
  HumanName,
  Patient,
  Reference,
  RelatedPerson,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { type CodingValue, codeableConcept } from '#src/codeableConcept.ts'

export const PERSON_RELATIONSHIP_TO_PATIENT_SYSTEM =
  'https://www.seenhealth.org/fhir/CodeSystem/person-relationship-to-patient'
export function makePersonRelationshipToPatientCoding(
  relationship: string
): CodingValue {
  return {
    system: PERSON_RELATIONSHIP_TO_PATIENT_SYSTEM,
    code: relationship,
    display: relationship,
  }
}
export function getPersonRelationshipToPatientFromCode(
  codes: CodeableConcept[] | undefined
): string[] {
  return compact(
    codes?.flatMap((code) =>
      code?.coding?.map((coding) =>
        coding.system === PERSON_RELATIONSHIP_TO_PATIENT_SYSTEM
          ? coding.code
          : null
      )
    )
  )
}

export interface FamilyContactInfo {
  name?: string
  phone?: string
  email?: string
}

export function makeFamilyContactRelatedPersonsForPatient(
  patientRef: Reference<Patient>,
  familyContactInformation: FamilyContactInfo[]
): RelatedPerson[] {
  // contact must be an empty array if no family contact information is provided
  const relatedPersons: RelatedPerson[] = []
  familyContactInformation.forEach((familyInfo) => {
    if (
      familyInfo.name === undefined &&
      familyInfo.phone === undefined &&
      familyInfo.email === undefined
    ) {
      return
    }

    let name: HumanName | undefined
    if (familyInfo.name) {
      // janky because right now, editing the questionnaire will mess with previous data
      // this brings up an interesting point around how to handle this
      const nameParts = familyInfo.name.split(' ')
      const family = nameParts.pop()
      name = {
        given: nameParts,
        family,
        use: 'usual',
      }
    }
    const telecom: ContactPoint[] = []
    if (familyInfo.phone) {
      telecom.push({
        system: 'phone',
        value: familyInfo.phone,
      })
    }
    if (familyInfo.email) {
      telecom.push({
        system: 'email',
        value: familyInfo.email,
      })
    }

    relatedPersons.push({
      resourceType: 'RelatedPerson',
      patient: patientRef,
      name: name ? [name] : undefined,
      telecom: telecom.length > 0 ? telecom : undefined,
      relationship: [
        codeableConcept([
          makePersonRelationshipToPatientCoding('Family'),
          makePersonRelationshipToPatientCoding('Contact'),
        ]),
      ],
    })
  })

  return relatedPersons
}
